import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

/**
 * Кастомный селект "Код исследования" с попапом-таблицей (как ResearchField),
 * загрузка услуг — по индикатору (groupId + subGroupName).
 *
 * Важно: компонент не изменяет данные сам — только отдаёт выбранный service объект наверх.
 */
export default function ResearchCodeSelectField({
  label = "Код исследования",
  disabled = false,
  groupId,
  indicatorName,
  valueServiceId,
  valueService,
  loadServices,
  onSelect,
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState([]);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open) return;
    if (disabled) return;
    if (!groupId || !indicatorName) {
      setServices([]);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const list = (await loadServices({ groupId, indicatorName })) || [];
        if (cancelled) return;
        // не выводим удалённые
        setServices((list || []).filter((s) => !s?.isDeleted && !s?.IsDeleted));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [disabled, groupId, indicatorName, loadServices, open]);

  const displayValue = useMemo(() => {
    const service = valueService || null;
    if (service) {
      const code = String(service?.code || "").trim();
      const name = String(service?.name || "").trim();
      return code ? (name ? `${code} - ${name}` : code) : name || "";
    }
    if (valueServiceId) return String(valueServiceId);
    return "";
  }, [valueService, valueServiceId]);

  const filtered = useMemo(() => {
    const q = String(query || "").trim().toLowerCase();
    if (!q) return services;
    return (services || []).filter((s) => {
      const hay = [
        s?.code,
        s?.name,
        s?.department?.name,
        s?.division?.name,
        s?.group?.name,
        s?.subGroup?.name,
        s?.kind?.name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [query, services]);

  return (
    <>
      <TextField
        fullWidth
        label={label}
        value={displayValue}
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setOpen(true);
        }}
        inputProps={{ readOnly: true }}
        placeholder="Нет выбора"
      />

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="xl"
      >
        <DialogTitle>Выбор кода исследования</DialogTitle>
        <Divider />
        <DialogContent>
          <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                Индикатор: <b>{indicatorName || "—"}</b>
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Поиск"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </Grid>
          </Grid>

          <div style={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                    Код исследования
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                    Наименование
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                    Лаборатория
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                    Отдел
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                    Группа
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                    Подгруппа
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                    Метод
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((s) => (
                  <TableRow
                    key={s?.id}
                    hover
                    onClick={() => {
                      if (disabled) return;
                      onSelect?.(s);
                      setOpen(false);
                    }}
                    sx={{ cursor: disabled ? "default" : "pointer" }}
                  >
                    <TableCell>{s?.code || "—"}</TableCell>
                    <TableCell>{s?.name || "—"}</TableCell>
                    <TableCell>{s?.department?.name || "—"}</TableCell>
                    <TableCell>{s?.division?.name || "—"}</TableCell>
                    <TableCell>{s?.group?.name || "—"}</TableCell>
                    <TableCell>{s?.subGroup?.name || "—"}</TableCell>
                    <TableCell>{s?.kind?.name || "—"}</TableCell>
                  </TableRow>
                ))}

                {!loading && filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      Нет исследований
                    </TableCell>
                  </TableRow>
                )}

                {loading && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      Загрузка...
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Отменить</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

